<template>
  <div class="tw-flex tw-gap-1 tw-justify-center tw-items-stretch tw-min-w-0">
    <button
      v-for="(seekObj, index) in seekObjects"
      :key="index"
      type="button"
      class="btn btn-light tw-relative tw-flex-none"
      :class="{ 'lt-seek-backward': seekObj.isBackwards, 'lt-seek-forward': !seekObj.isBackwards }"
      :aria-label="seekObj.label"
      @click="seekBy(seekObj.seconds)"
    >
      <icon-openmoji-forward class="lt-seek-icon tw-relative tw-text-lg" />
      <span class="lt-seek-label tw-text-xs tw-absolute">
        {{ seekObj.secondsAbs }}
      </span>
    </button>

    <input
      type="datetime-local"
      class="form-control tw-min-w-0 tw-text-center tw-flex-1 tw-order-2"
      :value="value"
      v-bind="attrs"
    />
  </div>
</template>

<script lang="ts" setup>
import { parseISO } from 'date-fns'
import { computed, defineComponent, useAttrs } from 'vue'
import { addSeconds } from './util'

defineComponent({
  inheritAttrs: false,
})
const props = defineProps<{
  modelValue: string
  seek?: boolean | number[]
}>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const attrs = useAttrs()
const value = computed<string>({
  get() {
    return formatLocalISO(parseISO(props.modelValue))
  },
  set(value) {
    emit('update:modelValue', parseLocalISO(value).toISOString())
  },
})
const seekObjects = computed(() => {
  const seek =
    props.seek === true
      ? [-60, -15, -1, 1, 15, 60]
      : props.seek === false
      ? []
      : [...(props.seek ?? [])].sort()
  return seek.map((seconds) => {
    const isBackwards = seconds < 0
    const secondsAbs = Math.abs(seconds)
    const label = isBackwards
      ? `${secondsAbs} Sekunden rückwärts springen`
      : `${secondsAbs} Sekunden vorwärts springen`
    return { isBackwards, label, seconds, secondsAbs }
  })
})

function pad(value: string | number, num = 2) {
  return String(value).padStart(num, '0')
}

function seekBy(seconds: number) {
  const newTime = addSeconds(parseISO(props.modelValue), seconds)
  emit('update:modelValue', newTime.toISOString())
}

function getTimezoneOffset(date?: Date) {
  const offset = (date ?? new Date()).getTimezoneOffset()
  const absOffset = Math.abs(offset)
  const minutes = absOffset % 60
  const hours = (absOffset - minutes) / 60
  const sign = offset <= 0 ? '+' : '-'
  return `${sign}${pad(hours)}:${pad(minutes)}`
}

function formatLocalISO(d: Date) {
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  return `${date}T${time}.${d.getMilliseconds()}`
}

function parseLocalISO(date: string) {
  const timezoneOffset = getTimezoneOffset()
  return parseISO(date + timezoneOffset)
}
</script>

<style lang="postcss" scoped>
.lt-seek-backward {
  order: 1;

  > .lt-seek-icon {
    transform: scaleX(-1);
    left: 5px;
    top: -2px;
  }

  > .lt-seek-label {
    text-align: right;
    right: 18px;
    top: 14px;
  }
}

.lt-seek-forward {
  order: 3;

  > .lt-seek-icon {
    top: -2px;
    right: 5px;
  }
  > .lt-seek-label {
    left: 18px;
    top: 14px;
  }
}
</style>
